/**
 * NoticeCreateModal (S6.1) — create a 出貨通知 for one vendor on one open
 * 裝櫃批次.
 *
 * Join chain: batch → its POs → vendors on those POs → the batch lines
 * belonging to the chosen vendor become the notice lines. Estimated
 * quantities default to the batch planned quantity and are editable — the
 * white slip 白單 notifies the factory with these figures.
 *
 * hide-don't-disable: a batch with no POs (or a vendor with no lines)
 * shows the explanation alert and the submit action is hidden.
 */

import { useEffect, useMemo, useState } from 'react';
import { App, Alert, Button, Col, InputNumber, Modal, Row, Select, Table, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { extractApiError } from '@/hooks/useSalesOrders';
import {
  useShipmentBatchOptions,
  useShippingNoticeMutations,
  useShippingPoOptions,
} from '@/hooks/useShipping';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';

const { Text } = Typography;

const noticeSchema = z.object({
  batchId: z.string().min(1, 'shipping:notice.validation.batchRequired'),
  vendorId: z.number().min(1, 'shipping:notice.validation.vendorRequired'),
});

type NoticeFormData = z.infer<typeof noticeSchema>;

const defaultFormValues: NoticeFormData = { batchId: '', vendorId: 0 };

interface DraftNoticeLine {
  poLineId: string;
  sku: string;
  qtyPlanned: number;
}

export interface NoticeCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create (page refreshes its list). */
  onCreated: () => void;
}

const NoticeCreateModal = ({ open, onClose, onCreated }: NoticeCreateModalProps) => {
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { batches, isLoading: batchesLoading } = useShipmentBatchOptions();
  const { purchaseOrders, isLoading: posLoading } = useShippingPoOptions();
  const { createNotice } = useShippingNoticeMutations();

  const [estimates, setEstimates] = useState<Record<string, number>>({});

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NoticeFormData>({
    resolver: zodResolver(noticeSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const batchId = useWatch({ control, name: 'batchId' });
  const vendorId = useWatch({ control, name: 'vendorId' });

  const selectedBatch = useMemo(
    () => batches.find((batch) => batch.id === batchId),
    [batches, batchId]
  );

  /** POs assigned to the selected batch. */
  const batchPos = useMemo(
    () =>
      selectedBatch
        ? purchaseOrders.filter((po) => selectedBatch.poIds.includes(po.id))
        : [],
    [purchaseOrders, selectedBatch]
  );

  /** Vendors with at least one PO on the batch (unique by vendorId). */
  const vendorOptions = useMemo(() => {
    const seen = new Map<number, string>();
    batchPos.forEach((po) => {
      if (!seen.has(po.vendorId)) seen.set(po.vendorId, po.vendorName);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [batchPos]);

  /** Batch lines belonging to the selected vendor's POs. */
  const draftLines: DraftNoticeLine[] = useMemo(() => {
    if (!selectedBatch || !vendorId) return [];
    const vendorPoIds = new Set(
      batchPos.filter((po) => po.vendorId === vendorId).map((po) => po.id)
    );
    return selectedBatch.lines
      .filter((line) => vendorPoIds.has(line.poId))
      .map((line) => ({ poLineId: line.poLineId, sku: line.sku, qtyPlanned: line.qtyPlanned }));
  }, [selectedBatch, batchPos, vendorId]);

  // Estimated quantities default to planned; reset on batch/vendor change.
  useEffect(() => {
    setEstimates(
      Object.fromEntries(draftLines.map((line) => [line.poLineId, line.qtyPlanned]))
    );
    // draftLines is derived from batchId/vendorId — keying on those avoids loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, vendorId, selectedBatch]);

  const closeModal = () => {
    reset(defaultFormValues);
    setEstimates({});
    onClose();
  };

  const onSubmit = async (values: NoticeFormData) => {
    const vendorName = vendorOptions.find((option) => option.value === values.vendorId)?.label;
    if (!vendorName || draftLines.length === 0) return;
    try {
      await createNotice({
        batchId: values.batchId,
        vendorId: values.vendorId,
        vendorName,
        lines: draftLines.map((line) => ({
          poLineId: line.poLineId,
          qtyEstimated: estimates[line.poLineId] ?? line.qtyPlanned,
        })),
      });
      message.success(t('shipping:notice.messages.created'));
      closeModal();
      onCreated();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.createFailed'));
    }
  };

  const fieldError = (key: keyof NoticeFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  const batchHasNoPos = Boolean(selectedBatch) && !posLoading && batchPos.length === 0;
  const vendorHasNoLines = Boolean(selectedBatch && vendorId) && draftLines.length === 0;
  const submitHidden = !batchId || batchHasNoPos || (Boolean(vendorId) && vendorHasNoLines);

  return (
    <Modal
      open={open}
      title={t('shipping:notice.modal.title')}
      onCancel={closeModal}
      width={800}
      footer={[
        <Button
          key="cancel"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={closeModal}
        >
          {t('common:actions.cancel')}
        </Button>,
        // hide-don't-disable: no submit while the join has nothing to notify
        ...(submitHidden
          ? []
          : [
              <Button
                key="create"
                type="primary"
                size="large"
                style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                loading={isSubmitting}
                onClick={() => void handleSubmit(onSubmit)()}
              >
                {t('shipping:notice.modal.submit')}
              </Button>,
            ]),
      ]}
    >
      <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:notice.modal.batchLabel')}
            </Text>
            <Controller
              name="batchId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || undefined}
                  onChange={(value) => {
                    field.onChange(value ?? '');
                    trigger('batchId');
                  }}
                  onBlur={field.onBlur}
                  options={batches
                    .filter((batch) => batch.status === 'open')
                    .map((batch) => ({ value: batch.id, label: batch.batchNumber }))}
                  loading={batchesLoading}
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('shipping:notice.modal.batchPlaceholder')}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.batchId ? 'error' : undefined}
                />
              )}
            />
            {fieldError('batchId')}
          </Col>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:notice.modal.vendorLabel')}
            </Text>
            <Controller
              name="vendorId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || undefined}
                  onChange={(value) => {
                    field.onChange(value ?? 0);
                    trigger('vendorId');
                  }}
                  onBlur={field.onBlur}
                  options={vendorOptions}
                  loading={posLoading}
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('shipping:notice.modal.vendorPlaceholder')}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.vendorId ? 'error' : undefined}
                />
              )}
            />
            {fieldError('vendorId')}
          </Col>
        </Row>
        {batchHasNoPos && (
          <Alert
            className="tw-mt-3"
            type="warning"
            showIcon
            message={t('shipping:notice.modal.noPosOnBatch')}
          />
        )}
        {vendorHasNoLines && !batchHasNoPos && (
          <Alert
            className="tw-mt-3"
            type="info"
            showIcon
            message={t('shipping:notice.modal.noLinesForVendor')}
          />
        )}
      </div>

      {draftLines.length > 0 && (
        <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('shipping:notice.modal.linesSection')}
          </Text>
          <Text
            type="secondary"
            style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
          >
            {t('shipping:notice.modal.linesHint')}
          </Text>
          <Table<DraftNoticeLine>
            size="small"
            rowKey="poLineId"
            dataSource={draftLines}
            pagination={false}
            columns={[
              {
                title: t('shipping:notice.modal.sku'),
                dataIndex: 'sku',
                key: 'sku',
                render: (value: string) => (
                  <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>
                ),
              },
              {
                title: t('shipping:notice.modal.qtyPlanned'),
                dataIndex: 'qtyPlanned',
                key: 'qtyPlanned',
                align: 'right',
                render: (value: number) => (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                ),
              },
              {
                title: t('shipping:notice.modal.qtyEstimated'),
                key: 'qtyEstimated',
                width: 160,
                render: (_: unknown, line) => (
                  <InputNumber
                    min={0}
                    value={estimates[line.poLineId] ?? line.qtyPlanned}
                    size="large"
                    style={{ width: '100%' }}
                    aria-label={t('shipping:notice.modal.qtyEstimatedFor', { sku: line.sku })}
                    onChange={(value) =>
                      setEstimates((prev) => ({ ...prev, [line.poLineId]: value ?? 0 }))
                    }
                  />
                ),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
};

export default NoticeCreateModal;
